import React from 'react';
import { useDecisionVoucherContext } from '../../context';
import { DecisionVoucherV2Step, DecisionVoucherV2Type } from '../../models/decisionVoucherV2';
import { useInfoDisplay, InfoDisplayType } from '../../context/InfoDisplayContext';
import { ContractInfo, VehicleInfo, CustomerInfo, QuoteViewer } from '../info';
import useCurrentSav from '../../../../../../Hooks/useCurrentSav';

const RightColumn: React.FC = () => {
  const { voucher } = useDecisionVoucherContext();
  const { currentDisplay, hideInfo } = useInfoDisplay();
  const { sav } = useCurrentSav();

  if (!voucher) {
    return (
      <div className="tw-p-3 tw-text-center">
        <p className="tw-text-gray-500">Chargement des informations...</p>
      </div>
    );
  }

  // Rendu des informations contextuelles si elles sont demandées
  const renderInfoComponent = () => {
    if (!sav) return null;

    switch (currentDisplay) {
      case InfoDisplayType.CONTRACT:
        return (
          <InfoContainer title="Informations contrat" onClose={hideInfo}>
            <ContractInfo contract={sav.contract} />
          </InfoContainer>
        );
      case InfoDisplayType.VEHICLE:
        return (
          <InfoContainer title="Informations véhicule" onClose={hideInfo}>
            <VehicleInfo contract={sav.contract} />
          </InfoContainer>
        );
      case InfoDisplayType.CUSTOMER:
        return (
          <InfoContainer title="Informations client" onClose={hideInfo}>
            <CustomerInfo contract={sav.contract} />
          </InfoContainer>
        );
      case InfoDisplayType.QUOTE:
        return (
          <InfoContainer title="Détails du devis" onClose={hideInfo}>
            <QuoteViewer
              quote={sav.acceptedQuote}
              amountDetails={sav.decisionVoucher?.amountDetails || []}
            />
          </InfoContainer>
        );
      default:
        return null;
    }
  };

  // Déterminer le contenu à afficher en fonction de l'étape
  const renderDefaultContent = () => {
    switch (voucher.step) {
      case DecisionVoucherV2Step.QUALIFICATION:
        return <QualificationPreview />;
      case DecisionVoucherV2Step.CONFIGURATION:
        return voucher.type === DecisionVoucherV2Type.PARTIAL_OR_TOTAL_AGREEMENT
          ? <AgreementPreview />
          : <RefusalPreview />;
      case DecisionVoucherV2Step.VALIDATION:
        return <FinalPreview />;
      default:
        return null;
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      <div className="tw-px-3 tw-py-3 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-medium tw-text-gray-800">
          {currentDisplay === InfoDisplayType.DEFAULT ? 'Aperçu' : 'Information'}
        </h3>
        <p className="tw-text-sm tw-text-gray-600">
          {currentDisplay === InfoDisplayType.DEFAULT
            ? 'Prévisualisation de votre décision'
            : 'Détails contextuels'}
        </p>
      </div>

      <div className="tw-p-3 tw-flex-grow tw-overflow-y-auto">
        {currentDisplay === InfoDisplayType.DEFAULT
          ? renderDefaultContent()
          : renderInfoComponent()}
      </div>

      <div className="tw-p-3 tw-border-t tw-border-gray-200">
        <SummaryInfo />
      </div>
    </div>
  );
};

// Conteneur pour les informations avec bouton de fermeture
interface InfoContainerProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}

const InfoContainer: React.FC<InfoContainerProps> = ({ title, children, onClose }) => {
  return (
    <div className="tw-space-y-3">
      <div className="tw-flex tw-justify-between tw-items-center">
        <h4 className="tw-text-sm tw-font-medium tw-text-blue-700">{title}</h4>
        <button
          onClick={onClose}
          className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-focus:outline-none tw-focus:text-gray-400"
          aria-label="Fermer"
        >
          <i className="fa-solid fa-times"></i>
        </button>
      </div>
      <div className="tw-bg-white tw-rounded">
        {children}
      </div>
    </div>
  );
};

// Prévisualisation pour l'étape de qualification
const QualificationPreview = () => {
  const { voucher } = useDecisionVoucherContext();

  const getStatusIcon = (value: boolean | null) => {
    if (value === null) return '❓';
    return value ? '✅' : '❌';
  };

  if (!voucher) return null;

  return (
    <div className="tw-space-y-3">
      <div className="tw-bg-gray-50 tw-rounded tw-p-3">
        <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Critères d'éligibilité</h4>

        <div className="tw-space-y-2">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-xs tw-text-gray-600">Contrat valide</span>
            <span>{getStatusIcon(voucher.qualification.isContractValid)}</span>
          </div>

          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-xs tw-text-gray-600">Kilométrage respecté</span>
            <span>{getStatusIcon(voucher.qualification.isKilometrageLimitRespected)}</span>
          </div>

          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-xs tw-text-gray-600">Délai de déclaration respecté</span>
            <span>{getStatusIcon(voucher.qualification.isDeclarationTimeRespected)}</span>
          </div>

          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-xs tw-text-gray-600">Pièces éligibles</span>
            <span>{getStatusIcon(voucher.qualification.arePartsEligible)}</span>
          </div>
        </div>
      </div>

      <div className="tw-bg-blue-50 tw-rounded tw-p-3">
        <p className="tw-text-xs tw-text-blue-700">
          Complétez tous les critères d'éligibilité pour passer à l'étape suivante.
        </p>
      </div>
    </div>
  );
};

// Prévisualisation pour l'accord
const AgreementPreview = () => {
  const { voucher } = useDecisionVoucherContext();

  if (!voucher) return null;

  return (
    <div className="tw-space-y-3">
      <div className="tw-bg-green-50 tw-rounded tw-p-3">
        <h4 className="tw-text-sm tw-font-medium tw-text-green-700 tw-mb-2">Accord</h4>
        <p className="tw-text-xs tw-text-green-600">
          Configuration en cours pour un accord partiel ou total.
        </p>
      </div>

      {/* Placeholder pour les détails de l'accord */}
      <div className="tw-bg-gray-50 tw-rounded tw-p-3">
        <h4 className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-2">Détails à venir</h4>
        <p className="tw-text-xs tw-text-gray-500">
          Les détails de l'accord seront affichés ici.
        </p>
      </div>
    </div>
  );
};

// Prévisualisation pour le refus
const RefusalPreview = () => {
  const { voucher } = useDecisionVoucherContext();

  if (!voucher) return null;

  return (
    <div className="tw-space-y-3">
      <div className="tw-bg-red-50 tw-rounded tw-p-3">
        <h4 className="tw-text-sm tw-font-medium tw-text-red-700 tw-mb-2">Refus</h4>
        <p className="tw-text-xs tw-text-red-600">
          Configuration en cours pour un refus.
        </p>
      </div>

      {/* Placeholder pour les détails du refus */}
      <div className="tw-bg-gray-50 tw-rounded tw-p-3">
        <h4 className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-2">Détails à venir</h4>
        <p className="tw-text-xs tw-text-gray-500">
          Les détails du refus seront affichés ici.
        </p>
      </div>
    </div>
  );
};

// Prévisualisation finale
const FinalPreview = () => {
  const { voucher } = useDecisionVoucherContext();

  if (!voucher) return null;

  return (
    <div className="tw-space-y-3">
      <div className="tw-bg-blue-50 tw-rounded tw-p-3">
        <h4 className="tw-text-sm tw-font-medium tw-text-blue-700 tw-mb-2">Prévisualisation</h4>
        <p className="tw-text-xs tw-text-blue-600">
          Aperçu du document avant envoi.
        </p>
      </div>

      {/* Placeholder pour la prévisualisation du document */}
      <div className="tw-bg-gray-50 tw-rounded tw-p-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[200px]">
        <svg className="tw-w-10 tw-h-10 tw-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
          La prévisualisation du document sera disponible ici.
        </p>
      </div>
    </div>
  );
};

// Informations récapitulatives
const SummaryInfo = () => {
  const { voucher } = useDecisionVoucherContext();

  if (!voucher) return null;

  return (
    <div className="tw-space-y-2">
      <h4 className="tw-text-sm tw-font-medium tw-text-gray-700">Résumé</h4>

      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-xs tw-text-gray-600">Type</span>
        <span className="tw-text-xs tw-font-medium tw-text-gray-800">
          {voucher.type === DecisionVoucherV2Type.PARTIAL_OR_TOTAL_AGREEMENT ? 'Accord' :
           voucher.type === DecisionVoucherV2Type.REFUSAL ? 'Refus' :
           voucher.type === DecisionVoucherV2Type.COMMERCIAL_GESTURE ? 'Geste commercial' :
           'En attente'}
        </span>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-xs tw-text-gray-600">Dernière modification</span>
        <span className="tw-text-xs tw-font-medium tw-text-gray-800">
          {voucher.updatedAt ? new Date(voucher.updatedAt).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : '-'}
        </span>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-xs tw-text-gray-600">Statut</span>
        <span className="tw-text-xs tw-font-medium tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-0.5 tw-rounded">
          Brouillon
        </span>
      </div>
    </div>
  );
};

export default RightColumn;